const initRolexMenus = (root = document) => {
  const menus = root.querySelectorAll('[data-rolex-menu-root]');

  menus.forEach((menuRoot) => {
    if (menuRoot.dataset.rolexMenuBound === 'true') return;

    const details = menuRoot.querySelector('[data-rolex-menu]');
    const summary = details?.querySelector('summary');
    const closeButtons = menuRoot.querySelectorAll('[data-rolex-menu-close]');
    const primaryItems = Array.from(menuRoot.querySelectorAll('.rolex-menu__primary-item'));
    const primaryList = menuRoot.querySelector('.rolex-menu__primary-list');
    const headerComponent =
      menuRoot.closest('#header-component') ||
      menuRoot.closest('header-component') ||
      document.querySelector('#header-component');
    const mobileQuery = window.matchMedia('(max-width: 989px)');

    if (!(details instanceof HTMLDetailsElement)) return;

    menuRoot.dataset.rolexMenuBound = 'true';
    details.classList.add('rolex-menu--intent');

    const getPreferredActiveItem = () =>
      primaryItems.find((item) => item.dataset.rolexMenuCurrent === 'true') || primaryItems[0];

    let activeItem = getPreferredActiveItem();
    let activationTimer;
    let closeTimer;
    let openTimer;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const closeDuration = reducedMotion ? 0 : 560;

    const getHeaderFillDuration = () => {
      if (reducedMotion || !headerComponent?.hasAttribute('transparent')) return 0;

      const underlay = headerComponent.querySelector('.header__underlay-closed');
      if (!underlay) return 0;

      const styles = window.getComputedStyle(underlay);
      const durations = styles.transitionDuration.split(',').map((value) => value.trim());
      const delays = styles.transitionDelay.split(',').map((value) => value.trim());
      const toMilliseconds = (value) => (value.endsWith('ms') ? Number.parseFloat(value) : Number.parseFloat(value) * 1000);

      return durations.reduce((maximum, duration, index) => {
        const delay = delays[index] || delays[0] || '0s';
        return Math.max(maximum, toMilliseconds(duration) + toMilliseconds(delay));
      }, 0);
    };

    const clearActivationTimer = () => {
      if (activationTimer) {
        window.clearTimeout(activationTimer);
        activationTimer = undefined;
      }
    };

    const clearOpenTimer = () => {
      if (openTimer) {
        window.clearTimeout(openTimer);
        openTimer = undefined;
      }
    };

    const setHeaderFilled = (filled) => {
      headerComponent?.classList.toggle('rolex-menu-header-filled', filled);
    };

    const cancelPendingOpen = () => {
      clearOpenTimer();
      details.classList.remove('is-preparing-open');
      document.documentElement.classList.remove('rolex-menu-is-open');
      setHeaderFilled(false);
    };

    const openMenu = () => {
      clearOpenTimer();
      window.clearTimeout(closeTimer);
      details.classList.remove('is-closing');
      details.classList.add('is-preparing-open');
      document.documentElement.classList.add('rolex-menu-is-open');
      setHeaderFilled(true);

      const revealMenu = () => {
        openTimer = undefined;
        details.classList.remove('is-preparing-open');
        setActiveItem(getPreferredActiveItem());
        details.setAttribute('open', '');
      };

      const fillDuration = getHeaderFillDuration();
      if (fillDuration > 0) {
        openTimer = window.setTimeout(revealMenu, fillDuration);
      } else {
        window.requestAnimationFrame(revealMenu);
      }
    };

    const updateMenuMetrics = () => {
      const headerBottom = headerComponent?.getBoundingClientRect().bottom;
      const panelTop = Number.isFinite(headerBottom)
        ? Math.max(0, Math.min(window.innerHeight, Math.round(headerBottom)))
        : 0;

      details.style.setProperty('--rolex-menu-top', `${panelTop}px`);

      if (!primaryList || mobileQuery.matches) {
        primaryList?.style.removeProperty('--rolex-menu-content-height');
        return;
      }

      const linksHeight = primaryItems.reduce((height, item) => height + item.getBoundingClientRect().height, 0);
      const panelHeight = primaryItems.reduce((maxHeight, item) => {
        const panel = item.querySelector('.rolex-menu__content-panel');
        return Math.max(maxHeight, panel?.scrollHeight || 0);
      }, 0);

      primaryList.style.setProperty(
        '--rolex-menu-content-height',
        `${Math.ceil(Math.max(linksHeight, panelHeight))}px`
      );
    };

    const setActiveItem = (item) => {
      if (!item) return;

      if (item === activeItem) {
        updateMenuMetrics();
        return;
      }

      activeItem?.classList.remove('is-active');
      item.classList.add('is-active');
      activeItem = item;
      updateMenuMetrics();
    };

    const scheduleActiveItem = (item) => {
      clearActivationTimer();

      if (!item || item === activeItem) return;

      activationTimer = window.setTimeout(() => {
        setActiveItem(item);
      }, 220);
    };

    const resetMobileItems = () => {
      primaryItems.forEach((item) => {
        item.classList.remove('is-mobile-open');
        item.querySelector('[data-rolex-menu-mobile-toggle]')?.setAttribute('aria-expanded', 'false');
      });
    };

    const setMobileItemOpen = (item, open) => {
      if (!item) return;

      primaryItems.forEach((otherItem) => {
        if (otherItem === item) return;

        otherItem.classList.remove('is-mobile-open');
        otherItem.querySelector('[data-rolex-menu-mobile-toggle]')?.setAttribute('aria-expanded', 'false');
      });

      item.classList.toggle('is-mobile-open', open);
      item.querySelector('[data-rolex-menu-mobile-toggle]')?.setAttribute('aria-expanded', String(open));

      if (open) {
        setActiveItem(item);
      }
    };

    if (activeItem) {
      activeItem.classList.add('is-active');
    }
    updateMenuMetrics();

    const closeMenu = () => {
      if (!details.open || details.classList.contains('is-closing')) return;

      clearOpenTimer();
      details.classList.remove('is-preparing-open');
      details.classList.add('is-closing');
      clearActivationTimer();
      resetMobileItems();

      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        details.removeAttribute('open');
        details.classList.remove('is-closing');
        document.documentElement.classList.remove('rolex-menu-is-open');
        setHeaderFilled(false);
      }, closeDuration);
    };

    summary?.addEventListener('click', (event) => {
      if (details.classList.contains('is-preparing-open')) {
        event.preventDefault();
        cancelPendingOpen();
        return;
      }

      if (!details.open) {
        event.preventDefault();
        openMenu();
        return;
      }

      event.preventDefault();

      if (details.classList.contains('is-closing')) {
        window.clearTimeout(closeTimer);
        details.classList.remove('is-closing');
        document.documentElement.classList.add('rolex-menu-is-open');
        setHeaderFilled(true);
        return;
      }

      closeMenu();
    });

    details.addEventListener('toggle', () => {
      if (details.open) {
        window.clearTimeout(closeTimer);
        details.classList.remove('is-closing');
        document.documentElement.classList.add('rolex-menu-is-open');
        setHeaderFilled(true);
        setActiveItem(getPreferredActiveItem());
        updateMenuMetrics();
        window.setTimeout(updateMenuMetrics, 0);
      } else {
        document.documentElement.classList.remove('rolex-menu-is-open');
        setHeaderFilled(false);
      }
      clearActivationTimer();
      if (!details.open) {
        resetMobileItems();
      }
    });

    closeButtons.forEach((button) => {
      button.addEventListener('click', closeMenu);
    });

    document.addEventListener(
      'click',
      (event) => {
        if (!details.isConnected || !details.open || details.classList.contains('is-closing')) return;

        const target = event.target;
        const panel = details.querySelector('.rolex-menu__panel');

        if (!(target instanceof Node) || !panel || details.contains(target)) return;
        if (event.clientY < panel.getBoundingClientRect().bottom) return;

        event.preventDefault();
        event.stopPropagation();
        closeMenu();
      },
      true
    );

    primaryItems.forEach((item) => {
      const link = item.querySelector('.rolex-menu__primary-link');
      const panel = item.querySelector('.rolex-menu__content-panel');
      const mobileToggle = item.querySelector('[data-rolex-menu-mobile-toggle]');

      link?.addEventListener('pointerenter', () => {
        scheduleActiveItem(item);
      });

      link?.addEventListener('click', (event) => {
        if (!mobileQuery.matches || !mobileToggle) return;

        event.preventDefault();
        setMobileItemOpen(item, !item.classList.contains('is-mobile-open'));
      });

      mobileToggle?.addEventListener('click', () => {
        setMobileItemOpen(item, !item.classList.contains('is-mobile-open'));
      });

      panel?.addEventListener('pointerenter', () => {
        clearActivationTimer();
        setActiveItem(item);
      });

      item.addEventListener('focusin', () => {
        clearActivationTimer();
        setActiveItem(item);
      });
    });

    menuRoot.addEventListener('pointerleave', clearActivationTimer);

    mobileQuery.addEventListener('change', (event) => {
      if (!event.matches) {
        resetMobileItems();
      }
      updateMenuMetrics();
    });

    window.addEventListener('resize', updateMenuMetrics, { passive: true });

    menuRoot.querySelectorAll('img').forEach((image) => {
      if (!image.complete) image.addEventListener('load', updateMenuMetrics, { once: true });
    });

    menuRoot.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    });

    if (details.open) {
      document.documentElement.classList.add('rolex-menu-is-open');
      setHeaderFilled(true);
    }
  });
};

initRolexMenus();

document.addEventListener('shopify:section:load', (event) => {
  initRolexMenus(event.target);
});
